/* eslint-disable complexity */

import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import type { Price } from 'types/isomorphic';
import acquisitionTypes from '../../support/acquisitionTypes';
import { defaultSizeIndex } from '../../shared/defaultSizeIndex';
import { SearchPreferencesCookie } from '../../cookies';
import { getPreferredPaId } from '../../../shared/utils/getPreferredPaId';
import { useAcquisitionType } from './useAcquisitionType';
import { selectedIndexQueryKey } from '../../shared/selectedIndexQueryKey';
import useOnLoad from '../queries/useOnLoad';
import useImageSizeOverride from '../useImageSizeOverride';
import useDownloadModifiedAsset from '../useDownloadModifiedAsset';
import { SIZE_NAMES } from '../../constants/sizeNames';

type Size = Price & {
  license?: string;
};

type Option = {
  id?: number;
  sizes?: Array<Size>;
  type?: string;
  isValid?: boolean;
};

/**
 * useSelectedIndex returns the size index and option index that the user has selected and functions to update them.
 * Also returns the selectedOption, selectedAgreementOptions, and selectedSize objects.
 *
 * @returns {{ selectedOption: Option, selectedOptionIndex: number, selectedSize: Size, selectedSizeIndex: number, setSelectedOptionIndex: function, setSelectedSizeIndex: function }}
 */

type SelectedIndices = {
  selectedOptionIndex: number;
  selectedSizeIndex: number;
};

const useSelectedIndex = () => {
  const queryClient = useQueryClient();
  const { response: { config: { options = [] } = {} } = {} } = useOnLoad() as {
    response?: { config?: { options?: Option[] } };
  };
  const { acquisitionType } = useAcquisitionType();
  const { teeShirtSize, eligibleImageSizeOverride } = useImageSizeOverride();
  const { assetLicense, genId } = useDownloadModifiedAsset();
  const isModifiedLicensedRedownload = !!genId && !!assetLicense;
  const queryKey = selectedIndexQueryKey(options?.[0]?.type, acquisitionType);

  const findIndexFromTeeShirtSize = (selectedOptionsIndex, size) =>
    options[selectedOptionsIndex]?.sizes?.findIndex(
      (s) => s.teeShirtSize === size
    );

  const getAgreementOptions = (selectedOption) => {
    let agreementOptions: Option[] = [];
    if (options?.length > 0) {
      const selectedOptionType = selectedOption?.type;
      agreementOptions = options.filter(
        (option) =>
          option.type === selectedOptionType &&
          (acquisitionType !== acquisitionTypes.DOWNLOAD || option.isValid)
      );
    }
    return agreementOptions;
  };

  const initialSelectedSizeIndex = (selectedOptionIndex) => {
    if (
      !options ||
      options.length === 0 ||
      !options[selectedOptionIndex]?.sizes?.length
    ) {
      return null;
    }

    const { sizes } = options[selectedOptionIndex];
    if (!sizes) return null;

    return defaultSizeIndex(sizes);
  };

  const initialSelectedOptionIndex = () => {
    if (
      acquisitionType !== acquisitionTypes.DOWNLOAD ||
      acquisitionType === acquisitionTypes.BATCH_DOWNLOAD ||
      !options?.length
    ) {
      return 0;
    }
    // Download acquisition type
    let selectedOption;
    let selectedOptionIndex: number | null = null;
    const searchPreferencesCookie = SearchPreferencesCookie.find() as {
      saved_filters?: string;
    };
    const preferredPaId = getPreferredPaId(
      searchPreferencesCookie.saved_filters
    );
    const cookieSelectedOption = options.find(
      (option) => option.id === parseInt(preferredPaId ?? '', 10)
    );
    if (cookieSelectedOption && cookieSelectedOption.isValid) {
      selectedOption = cookieSelectedOption;
    } else {
      selectedOption = options.find((option) => option.isValid);
    }

    options.forEach((option, index) => {
      if (selectedOption?.id === option.id) {
        selectedOptionIndex = index;
      }
    });
    return selectedOptionIndex || 0;
  };

  const getInitialSelectedSizeIndex = (selectedOptionIndex) => {
    if (isModifiedLicensedRedownload) {
      const index = options[selectedOptionIndex]?.sizes?.findIndex(
        (size) => size.name === assetLicense.sizeName
      );
      if (typeof index !== 'undefined' && index > -1) {
        return index;
      }
      if (assetLicense.sizeName === SIZE_NAMES.extraSmall) {
        return options[selectedOptionIndex]?.sizes?.findIndex(
          (size) => size.name === SIZE_NAMES.small
        );
      }
    }
    if (eligibleImageSizeOverride) {
      const index = findIndexFromTeeShirtSize(
        selectedOptionIndex,
        teeShirtSize
      );
      if (typeof index !== 'undefined' && index > -1) {
        return index;
      }
    }
    return initialSelectedSizeIndex(selectedOptionIndex);
  };

  const getSizeIndices = () => {
    const selectedOptionIndex = initialSelectedOptionIndex();
    const selectedSizeIndex = getInitialSelectedSizeIndex(selectedOptionIndex);
    return {
      selectedOptionIndex,
      selectedSizeIndex,
    };
  };

  const { data } = useSuspenseQuery({
    queryKey,
    queryFn: getSizeIndices,
  });

  const setSelectedIndex = (
    { selectedOptionIndex, selectedSizeIndex }: Partial<SelectedIndices>,
    currentOptions: Array<{ sizes?: Pick<Price, 'isDefault'>[] }> = options
  ) => {
    queryClient.setQueryData(queryKey, (oldData: SelectedIndices) => {
      let newSelectedSizeIndex =
        selectedSizeIndex ?? oldData?.selectedSizeIndex;
      const newSelectedOptionIndex =
        selectedOptionIndex ?? oldData?.selectedOptionIndex;
      const newSelectedOptionSizes =
        currentOptions[newSelectedOptionIndex]?.sizes;
      if (newSelectedOptionSizes && newSelectedOptionSizes.length > 0) {
        newSelectedSizeIndex = newSelectedOptionSizes[newSelectedSizeIndex]
          ? newSelectedSizeIndex
          : defaultSizeIndex(newSelectedOptionSizes);
      }
      return {
        selectedOptionIndex: newSelectedOptionIndex,
        selectedSizeIndex: newSelectedSizeIndex,
      };
    });
  };

  const setSelectedOptionIndex = (selectedOptionIndex: number) => {
    setSelectedIndex({ selectedOptionIndex });
  };

  const setSelectedSizeIndex = (selectedSizeIndex: number) => {
    setSelectedIndex({ selectedSizeIndex });
  };

  let selectedOption = {} as Option;
  if (
    options?.length > 0 &&
    typeof data?.selectedOptionIndex !== 'undefined' &&
    data.selectedOptionIndex >= 0
  ) {
    selectedOption = options[data.selectedOptionIndex];
  }
  let selectedSize = {} as Size;
  if (
    selectedOption?.sizes &&
    selectedOption.sizes.length > 0 &&
    typeof data?.selectedSizeIndex === 'number' &&
    data.selectedSizeIndex >= 0
  ) {
    selectedSize = selectedOption.sizes[data.selectedSizeIndex];
  }

  const selectedAgreementOptions = getAgreementOptions(selectedOption);

  // Select a size by its size-code name within the current option. Used by the
  // modified buy card's original-image selector so the licensed size (and its
  // price/sizeCode) follows the user's choice.
  const setSelectedSizeByName = (name: string) => {
    const sizes = selectedOption?.sizes;
    if (!sizes) return;
    const index = sizes.findIndex((s) => s.name === name);
    if (index > -1) {
      setSelectedSizeIndex(index);
    }
  };

  return {
    selectedOptionIndex: data?.selectedOptionIndex,
    selectedSizeIndex: data?.selectedSizeIndex,
    selectedOption,
    selectedAgreementOptions,
    selectedSize,
    setSelectedIndex,
    setSelectedOptionIndex,
    setSelectedSizeIndex,
    setSelectedSizeByName,
  };
};

export default useSelectedIndex;
